if (!customElements.get("sticky-header")) {
  customElements.define(
    "sticky-header",
    class StickyHeader extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.stickyDisabled = this.getAttribute("disabled") === "true";
        this.header = document.querySelector(".section-header");
        this.headerElement = this.querySelector(".header");
        this.alwaysSticky = this.dataset.alwaysOn === "true";
        this.smartScroll = this.dataset.smartScroll !== "false";
        this.stickyStyle = this.dataset.stickyStyle || "full_width";
        this.headerBounds = {};
        this.currentScrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
        this.lastAppliedScrollTop = this.currentScrollTop;
        this.preventReveal = false;
        this.ticking = false;
        this.scrollDelta = 6;
        this.mainNavToggle = this.querySelector("main-nav-toggle");
        this.predictiveSearch = this.querySelector("predictive-search");
        this.desktopMediaQuery = window.matchMedia("(min-width: 990px)");
        this.reduceMotionMediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");

        if (!this.header) {
          return;
        }

        this.onScrollHandler = this.onScroll.bind(this);
        this.onResizeHandler = this.onResize.bind(this);
        this.onMediaChangeHandler = this.syncStrategy.bind(this);
        this.hideHeaderOnScrollUp = () => {
          this.preventReveal = true;
        };

        this.addEventListener("preventHeaderReveal", this.hideHeaderOnScrollUp);
        window.addEventListener("scroll", this.onScrollHandler, { passive: true });
        window.addEventListener("resize", this.onResizeHandler, { passive: true });
        window.addEventListener("orientationchange", this.onResizeHandler, { passive: true });

        if (typeof this.desktopMediaQuery.addEventListener === "function") {
          this.desktopMediaQuery.addEventListener("change", this.onMediaChangeHandler);
        } else if (typeof this.desktopMediaQuery.addListener === "function") {
          this.desktopMediaQuery.addListener(this.onMediaChangeHandler);
        }

        this.createObserver();
        this.measureHeader();
        this.syncStrategy();
      }

      disconnectedCallback() {
        this.removeEventListener("preventHeaderReveal", this.hideHeaderOnScrollUp);
        window.removeEventListener("scroll", this.onScrollHandler);
        window.removeEventListener("resize", this.onResizeHandler);
        window.removeEventListener("orientationchange", this.onResizeHandler);

        if (this.resizeObserver) {
          this.resizeObserver.disconnect();
        }

        if (typeof this.desktopMediaQuery?.removeEventListener === "function") {
          this.desktopMediaQuery.removeEventListener("change", this.onMediaChangeHandler);
        } else if (typeof this.desktopMediaQuery?.removeListener === "function") {
          this.desktopMediaQuery.removeListener(this.onMediaChangeHandler);
        }
      }

      createObserver() {
        if ("IntersectionObserver" in window) {
          const observer = new IntersectionObserver((entries, observerInstance) => {
            this.headerBounds = entries[0].intersectionRect;
            observerInstance.disconnect();
          });

          observer.observe(this.header);
        }

        if ("ResizeObserver" in window) {
          this.resizeObserver = new ResizeObserver(() => this.measureHeader());
          this.resizeObserver.observe(this.header);
          if (this.headerElement) {
            this.resizeObserver.observe(this.headerElement);
          }
        }
      }

      isDesktop() {
        return this.desktopMediaQuery && this.desktopMediaQuery.matches;
      }

      shouldUsePillShape(scrollTop = this.currentScrollTop, scrollDelta = 0) {
        if (this.stickyStyle === "pill") {
          return true;
        }

        if (this.stickyStyle !== "transition") {
          return false;
        }

        const wasFloating = this.header?.classList.contains("section-header-sticky");

        if (!wasFloating) {
          return scrollTop > this.floatEnterThreshold;
        }

        if (scrollDelta < 0) {
          return scrollTop > this.floatReturnThreshold;
        }

        return scrollTop > this.floatExitThreshold;
      }

      applyHeaderShape(scrollTop = this.currentScrollTop, scrollDelta = 0) {
        if (!this.header || this.stickyDisabled) {
          return;
        }

        const shouldUsePill = this.shouldUsePillShape(scrollTop, scrollDelta);
        this.header.classList.toggle("section-header-sticky", shouldUsePill);
        this.header.classList.toggle("animate", shouldUsePill);
      }

      shouldAutoHide() {
        // Desktop has enough room for persistent navigation/search/cart access.
        // Tablet and mobile get the cleaner content-first behavior: hide while scrolling down, reveal on scroll up.
        return this.smartScroll && !this.stickyDisabled && !this.isDesktop();
      }

      measureHeader() {
        const boundsHeight = this.headerBounds?.bottom || 0;
        const sectionHeight = this.header?.getBoundingClientRect().height || 0;
        const elementHeight = this.headerElement?.getBoundingClientRect().height || 0;
        this.headerHeight = Math.ceil(Math.max(boundsHeight, sectionHeight, elementHeight, 72));
        this.hideThreshold = Math.max(92, this.headerHeight + 8);

        // Transition mode trigger. Keep the deadband small so the full-width/pill
        // state changes in the same general area in both scroll directions.
        this.floatEnterThreshold = Math.max(112, this.headerHeight + 44);
        this.floatReturnThreshold = Math.max(104, this.floatEnterThreshold - 8);
        this.floatExitThreshold = Math.max(28, this.headerHeight - 16);
        this.floatThreshold = this.floatEnterThreshold;
      }

      onResize() {
        window.requestAnimationFrame(() => {
          this.measureHeader();
          this.syncStrategy();
        });
      }

      syncStrategy() {
        if (!this.header || this.stickyDisabled) {
          return;
        }

        this.measureHeader();

        if (this.isDesktop()) {
          this.header.classList.add("section-header-sticky-always");
          this.header.classList.remove("section-header-hidden");
          this.applyHeaderShape(this.currentScrollTop, 0);

          if (this.mainNavToggle) {
            this.mainNavToggle.disable();
          }
          return;
        }

        this.header.classList.remove("section-header-sticky-always");

        if (this.currentScrollTop <= this.hideThreshold) {
          this.reset();
        }
      }

      onScroll() {
        if (this.ticking) {
          return;
        }

        this.ticking = true;
        window.requestAnimationFrame(() => {
          this.updateHeaderOnScroll();
          this.ticking = false;
        });
      }

      updateHeaderOnScroll() {
        const scrollTop = Math.max(0, window.pageYOffset || document.documentElement.scrollTop || 0);
        const scrollDelta = scrollTop - this.currentScrollTop;

        if (Math.abs(scrollDelta) < this.scrollDelta) {
          return;
        }

        if (this.predictiveSearch && this.predictiveSearch.isOpen) {
          this.currentScrollTop = scrollTop;
          return;
        }

        if (this.isHeaderInteractionOpen()) {
          this.reveal();
          this.currentScrollTop = scrollTop;
          return;
        }

        if (!this.shouldAutoHide()) {
          this.keepDesktopSticky(scrollTop, scrollDelta);
          this.currentScrollTop = scrollTop;
          return;
        }

        if (scrollTop <= this.hideThreshold) {
          this.reset();
        } else if (scrollDelta > 0) {
          this.hide();
        } else if (scrollDelta < 0) {
          if (!this.preventReveal) {
            this.reveal();
          } else {
            window.clearTimeout(this.isScrolling);

            this.isScrolling = setTimeout(() => {
              this.preventReveal = false;
            }, 90);

            this.hide();
          }
        }

        this.currentScrollTop = scrollTop;
      }

      keepDesktopSticky(scrollTop, scrollDelta = 0) {
        if (this.stickyDisabled) {
          if (scrollTop <= this.hideThreshold) {
            this.reset();
          }
          return;
        }

        this.header.classList.add("section-header-sticky-always");
        this.header.classList.remove("section-header-hidden");

        this.applyHeaderShape(scrollTop, scrollDelta);

        if (this.mainNavToggle) {
          this.mainNavToggle.disable();
        }
      }

      isHeaderInteractionOpen() {
        const navigationWrapper = document.querySelector(".navigation-wrapper");
        if (navigationWrapper && navigationWrapper.classList.contains("is-animating")) {
          return true;
        }

        const collapseHost = document.querySelector("#header.header-nav-collapse-enabled");
        if (collapseHost && !collapseHost.classList.contains("navigation-collapsed")) {
          return true;
        }

        if (document.body.classList.contains("mobile-menu-open")) {
          return true;
        }

        if (document.body.classList.contains("drawer-open")) {
          return true;
        }

        if (this.header.querySelector("details[open], drawer-modal[open], header-search-dropdown-modal[open], localization-form [aria-expanded='true']")) {
          return true;
        }

        return false;
      }

      hide() {
        if (!this.shouldAutoHide()) {
          return;
        }

        this.header.classList.add("section-header-hidden");
        this.applyHeaderShape(this.currentScrollTop, 1);

        if (this.mainNavToggle) {
          this.mainNavToggle.enable();
        }

        this.closeHeaderDropdowns();
      }

      reveal() {
        if (this.stickyDisabled) {
          return;
        }

        this.header.classList.remove("section-header-hidden");
        this.applyHeaderShape(this.currentScrollTop, -1);
      }

      reset() {
        this.header.classList.remove("section-header-hidden");
        this.applyHeaderShape(0, 0);

        if (!this.shouldAutoHide() && !this.stickyDisabled) {
          this.header.classList.add("section-header-sticky-always");
        } else {
          this.header.classList.remove("section-header-sticky-always");
        }

        if (this.mainNavToggle) {
          this.mainNavToggle.disable();
        }
      }

      closeHeaderDropdowns() {
        const headerDropdowns = this.header.querySelectorAll("localization-form");

        headerDropdowns.forEach((dropdown) => {
          if (typeof dropdown.hidePanel === "function") {
            dropdown.hidePanel();
          }
        });
      }
    },
  );
}

if (!customElements.get("main-nav-toggle")) {
  customElements.define(
    "main-nav-toggle",
    class MainNavToggle extends HTMLElement {
      constructor() {
        super();
        this.header = document.querySelector("#header");
        this.stickyHeader = document.querySelector("sticky-header");
        this.button = this.querySelector(".button-main-nav-toggle");
        this.navigationWrapper = document.querySelector(".navigation-wrapper");

        if (!this.navigationWrapper) {
          return;
        }

        this.navigationWrapper.style.setProperty(
          "--navigation-height",
          this.navigationWrapper.clientHeight + 2 + "px",
        );

        this.button.addEventListener("click", () => this.onToggle());
      }

      onToggle(state) {
        if (this.navigationWrapper.classList.contains("is-animating") || !this.stickyHeader) {
          return;
        }

        this.navigationWrapper.classList.add("is-animating");

        if (state != null) {
          if (state === "collapse") {
            this.collapse();
          } else if (state === "expand") {
            this.expand();
          }
        } else {
          this.isCollapsed() ? this.expand() : this.collapse();
        }

        this.syncHeaderReveal();

        setTimeout(() => {
          this.navigationWrapper.classList.remove("is-animating");
        }, 300);
      }

      syncHeaderReveal() {
        const section = document.querySelector(".section-header");
        if (!section) {
          return;
        }

        const menuOpen =
          this.header.classList.contains("header-nav-collapse-enabled") && !this.isCollapsed();

        if (menuOpen) {
          section.classList.remove("section-header-hidden");
          section.classList.add("menu-open");
        } else {
          section.classList.remove("menu-open");
        }
      }

      enable() {
        if (!this.header.classList.contains("header-nav-collapse-enabled")) {
          this.header.classList.add("header-nav-collapse-enabled");
          this.collapse();
        }
      }

      disable() {
        if (this.header.classList.contains("header-nav-collapse-enabled")) {
          this.header.classList.remove("header-nav-collapse-enabled");
          this.expand();
        }
      }

      expand() {
        this.header.classList.remove("navigation-collapsed");
      }

      collapse() {
        this.classList.add("is-visible");
        this.header.classList.add("navigation-collapsed");
      }

      isCollapsed() {
        return this.header.classList.contains("navigation-collapsed");
      }
    },
  );
}